"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from "react";
import { Link } from "@/components/LocaleLink";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, Chrome, ExternalLink, Loader2, ShieldOff, CheckCircle2, CircleDashed } from "lucide-react";
import { useAuthStore } from "@/lib/store";
import { useTranslation } from "@/lib/hooks/useTranslation";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
type ExtensionStatus = {
  is_connected: boolean;
  last_authorized_at: string | null;
  revoked_at: string | null;
};
const CHROME_STORE_URL = process.env.NEXT_PUBLIC_EXTENSION_STORE_URL || "https://chromewebstore.google.com/search/velyq";
export default function ExtensionSettingsPage() {
  const {
    accessToken
  } = useAuthStore();
  const {
    locale
  } = useTranslation();
  const isFr = locale === "fr";
  const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";
  const [status, setStatus] = useState<ExtensionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRevoking, setIsRevoking] = useState(false);
  const loadStatus = useCallback(async () => {
    if (!accessToken) return;
    try {
      const res = await fetch(`${API_URL}/api/v1/auth/extension-status`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      if (res.ok) {
        setStatus(await res.json());
      } else {
        setStatus({
          is_connected: false,
          last_authorized_at: null,
          revoked_at: null
        });
      }
    } catch {
      setStatus({
        is_connected: false,
        last_authorized_at: null,
        revoked_at: null
      });
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, API_URL]);
  useEffect(() => {
    loadStatus();
  }, [loadStatus]);
  const handleRevoke = async () => {
    if (!accessToken) return;
    setIsRevoking(true);
    try {
      const res_0 = await fetch(`${API_URL}/api/v1/auth/extension-token`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      if (res_0.ok) {
        toast.success(isFr ? "Accès de l'extension révoqué" : "Extension access revoked");
        await loadStatus();
      } else {
        throw new Error("Revoke failed");
      }
    } catch {
      toast.error(isFr ? "Échec de la révocation" : "Revoke failed");
    } finally {
      setIsRevoking(false);
    }
  };
  const formatDate = (iso: string | null) => {
    if (!iso) return isFr ? "Jamais" : "Never";
    const d = new Date(iso);
    return d.toLocaleString(isFr ? "fr-FR" : "en-US", {
      dateStyle: "medium",
      timeStyle: "short"
    });
  };
  return <div className="min-h-screen bg-background">
      <header className="border-b bg-background/95 backdrop-blur">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <Link href="/">
            <Logo />
          </Link>
          <Link href="/settings">
            <Button variant="ghost" size="sm" className="gap-1.5">
              <ArrowLeft className="h-4 w-4" />
              {isFr ? "Réglages" : "Settings"}
            </Button>
          </Link>
        </div>
      </header>

      <main className="container mx-auto max-w-3xl px-4 py-10">
        <div className="mb-6 flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2">
            <Chrome className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">
              {isFr ? "Extension Chrome" : "Chrome extension"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isFr ? "Gère la connexion entre ton compte Velyq et l'extension." : "Manage the connection between your Velyq account and the extension."}
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{isFr ? "Statut" : "Status"}</CardTitle>
            <CardDescription>
              {isFr ? "Informations sur l'autorisation active de l'extension." : "Info about the currently authorized extension session."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoading ? <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {isFr ? "Chargement…" : "Loading…"}
              </div> : status?.is_connected ? <StatusRow icon={<CheckCircle2 className="h-5 w-5 text-emerald-500" />} title={isFr ? "Extension connectée" : "Extension connected"} subtitle={`${isFr ? "Dernière autorisation : " : "Last authorized: "}${formatDate(status.last_authorized_at)}`} /> : <StatusRow icon={<CircleDashed className="h-5 w-5 text-muted-foreground" />} title={isFr ? "Aucune extension connectée" : "No extension connected"} subtitle={status?.revoked_at ? `${isFr ? "Révoquée le " : "Revoked on "}${formatDate(status.revoked_at)}` : isFr ? "Installe l'extension pour clipper tes offres en 1 clic." : "Install the extension to clip offers in one click."} />}
          </CardContent>
        </Card>

        {status?.is_connected ? <Card className="mt-6 border-destructive/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldOff className="h-4 w-4 text-destructive" />
                {isFr ? "Révoquer l'accès" : "Revoke access"}
              </CardTitle>
              <CardDescription>
                {isFr ? "Invalide immédiatement les tokens de l'extension sur tous les appareils. Tu pourras la reconnecter à tout moment." : "Immediately invalidates extension tokens on every device. You can reconnect it any time."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" disabled={isRevoking} className="gap-2">
                    {isRevoking ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldOff className="h-4 w-4" />}
                    {isFr ? "Révoquer l'accès" : "Revoke access"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {isFr ? "Révoquer l'extension ?" : "Revoke extension?"}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {isFr ? "L'extension sera déconnectée et ne pourra plus clipper ni remplir de formulaires. Tes candidatures déjà enregistrées restent intactes." : "The extension will be signed out and can no longer clip offers or fill forms. Your saved applications are untouched."}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>
                      {isFr ? "Annuler" : "Cancel"}
                    </AlertDialogCancel>
                    <AlertDialogAction onClick={handleRevoke}>
                      {isFr ? "Révoquer" : "Revoke"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card> : <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-base">
                {isFr ? "Installer l'extension" : "Install the extension"}
              </CardTitle>
              <CardDescription>
                {isFr ? "Clippe une offre depuis LinkedIn, Indeed ou Welcome to the Jungle en 1 clic." : "Clip offers from LinkedIn, Indeed or Welcome to the Jungle in one click."}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <a href={CHROME_STORE_URL} target="_blank" rel="noreferrer">
                <Button className="gap-2">
                  <Chrome className="h-4 w-4" />
                  {isFr ? "Ajouter à Chrome" : "Add to Chrome"}
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </a>
              <Link href="/extension">
                <Button variant="outline">
                  {isFr ? "En savoir plus" : "Learn more"}
                </Button>
              </Link>
            </CardContent>
          </Card>}

        <p className="mt-8 text-xs text-muted-foreground">
          {isFr ? "Politique de confidentialité de l'extension : " : "Extension privacy policy: "}
          <Link href="/privacy/extension" className="text-primary underline">
            /privacy/extension
          </Link>
        </p>
      </main>
    </div>;
}
function StatusRow(t0) {
  const $ = _c(12);
  const {
    icon,
    title,
    subtitle
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <div className="mt-0.5">{icon}</div>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <div className="font-medium">{title}</div>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== subtitle) {
    t3 = <div className="text-sm text-muted-foreground">{subtitle}</div>;
    $[4] = subtitle;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div>{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t4) {
    t5 = <div className="flex items-start gap-3">{t1}{t4}</div>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}